import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { format, parseISO, differenceInCalendarDays, startOfToday, addDays } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Calendar, ExternalLink } from "lucide-react";
import DatePicker from "@/components/DatePicker";
import { toast } from "sonner";

export default function Planner() {
  const [tasks, setTasks] = useState([]);
  const [countdowns, setCountdowns] = useState([]);
  const [events, setEvents] = useState([]);
  const [cals, setCals] = useState([]);
  const [open, setOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [cd, setCd] = useState({ title: "", date: "" });
  const [calForm, setCalForm] = useState({ name: "", ics_url: "", color: "#9D4EDD" });

  const load = async () => {
    setTasks((await api.get("/tasks", { params: { due: "week" } })).data);
    setCountdowns((await api.get("/countdowns")).data);
    setCals((await api.get("/calendars")).data);
    try { setEvents((await api.get("/calendars/events")).data); } catch { setEvents([]); }
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => { const t = startOfToday(); return [...Array(7)].map((_, i) => addDays(t, i)); }, []);
  const byDay = useMemo(() => {
    const m = {};
    for (const d of days) m[format(d, "yyyy-MM-dd")] = { tasks: [], events: [] };
    for (const t of tasks) if (t.due_date && m[t.due_date]) m[t.due_date].tasks.push(t);
    for (const e of events) if (m[e.date]) m[e.date].events.push(e);
    return m;
  }, [tasks, days, events]);

  const createCd = async () => {
    if (!cd.title || !cd.date) return;
    await api.post("/countdowns", cd);
    setCd({ title: "", date: "" }); setOpen(false); load();
  };
  const createCal = async () => {
    if (!calForm.name.trim() || !calForm.ics_url.trim()) return;
    try {
      await api.post("/calendars", calForm);
      toast.success("Calendar linked");
      setCalForm({ name: "", ics_url: "", color: "#9D4EDD" }); setCalOpen(false); load();
    } catch { toast.error("Could not link calendar"); }
  };
  const delCal = async (id) => { await api.delete(`/calendars/${id}`); load(); };

  return (
    <div className="min-h-screen px-10 pt-10 pb-16">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="serif-italic text-5xl text-white/85">Planner</h1>
        <div className="flex gap-2">
          <button onClick={() => setCalOpen(true)} className="pill" data-testid="link-gcal"><Calendar size={13} /> Link Google Calendar</button>
          <button onClick={() => setOpen(true)} className="pill" data-testid="add-countdown"><Plus size={13} /> Countdown</button>
        </div>
      </div>
      <div className="h-px bg-white/10 mb-8" />

      {cals.length > 0 && (
        <div className="mb-8 flex flex-wrap gap-2">
          {cals.map(c => (
            <div key={c.cal_id} className="pill text-xs flex items-center gap-2" data-testid={`cal-${c.cal_id}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: c.color }} />
              {c.name} <span className="text-white/40">· {events.filter(e => e.calendar === c.name).length} events</span>
              <button onClick={() => delCal(c.cal_id)} className="text-white/40 hover:text-white ml-1"><Trash2 size={11} /></button>
            </div>
          ))}
        </div>
      )}

      {countdowns.length > 0 && (
        <div className="mb-10">
          <div className="text-xs uppercase tracking-widest text-white/40 mb-3">Countdowns</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {countdowns.map(c => {
              const d = differenceInCalendarDays(parseISO(c.date), startOfToday());
              return (
                <div key={c.countdown_id} className="card-base p-5 flex justify-between items-start" data-testid={`countdown-${c.countdown_id}`}>
                  <div>
                    <div className="text-white/70 text-sm">{c.title}</div>
                    <div className="serif-italic text-4xl mt-1" style={{ color: "var(--orange)" }}>{d}</div>
                    <div className="text-xs text-white/40 mt-1">days · {format(parseISO(c.date), "MMM d, yyyy")}</div>
                  </div>
                  <button onClick={async () => { await api.delete(`/countdowns/${c.countdown_id}`); load(); }} className="text-white/30 hover:text-white/70"><Trash2 size={13} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-xs uppercase tracking-widest text-white/40 mb-3">This week</div>
      <div className="grid grid-cols-7 gap-3">
        {days.map(d => {
          const key = format(d, "yyyy-MM-dd");
          const items = byDay[key] || { tasks: [], events: [] };
          const isToday = key === format(startOfToday(), "yyyy-MM-dd");
          return (
            <div key={key} className="card-base p-3 min-h-[220px]" data-testid={`day-${key}`}>
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-xs text-white/50">{format(d, "EEE")}</div>
                <div className={`serif-italic text-xl ${isToday ? "" : "text-white/70"}`} style={isToday ? { color: "var(--orange)" } : {}}>{format(d, "d")}</div>
              </div>
              <div className="space-y-1.5">
                {items.events.map((e, i) => (
                  <div key={`e${i}`} className="text-xs text-white/85 truncate" title={e.summary}>
                    <span className="inline-block w-1 h-1 rounded-full mr-1.5 align-middle" style={{ background: e.color }} />{e.summary}
                  </div>
                ))}
                {items.tasks.map(t => (
                  <div key={t.task_id} className="text-xs text-white/80 truncate">
                    <span className="inline-block w-1 h-1 rounded-full mr-1.5 align-middle" style={{ background: "var(--orange)" }} />{t.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-white/10">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">New countdown</DialogTitle></DialogHeader>
          <Input value={cd.title} onChange={e => setCd({ ...cd, title: e.target.value })} placeholder="Trip to Iceland" className="bg-white/5 border-white/10" data-testid="cd-title" />
          <DatePicker value={cd.date} onChange={(v) => setCd({ ...cd, date: v })} placeholder="Event date" testId="cd-date" />
          <div className="flex justify-end"><Button onClick={createCd} style={{ background: "var(--orange)" }} data-testid="cd-save">Save</Button></div>
        </DialogContent>
      </Dialog>

      <Dialog open={calOpen} onOpenChange={setCalOpen}>
        <DialogContent className="bg-[color:var(--surface)] border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="serif-italic text-2xl">Link Google Calendar</DialogTitle></DialogHeader>
          <div className="text-sm text-white/60 space-y-2">
            <p>Paste your <b>secret iCal address</b> (URL ending in <span className="mono text-xs">.ics</span>). Read-only, no OAuth needed.</p>
            <p className="text-white/40 text-xs">Google Calendar → Settings → your calendar → “Secret address in iCal format”. <a href="https://calendar.google.com/calendar/u/0/r/settings" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[--orange]" style={{ color: "var(--orange)" }}>Open settings <ExternalLink size={10} /></a></p>
          </div>
          <Input placeholder="Calendar name (e.g. Work)" value={calForm.name} onChange={e => setCalForm({ ...calForm, name: e.target.value })} className="bg-white/5 border-white/10" data-testid="cal-name" />
          <Input placeholder="https://calendar.google.com/calendar/ical/…/basic.ics" value={calForm.ics_url} onChange={e => setCalForm({ ...calForm, ics_url: e.target.value })} className="bg-white/5 border-white/10 mono text-xs" data-testid="cal-url" />
          <div className="flex justify-end"><Button onClick={createCal} style={{ background: "var(--orange)" }} data-testid="cal-save">Link</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
